import React, { useMemo } from 'react';
import { View, Text, ScrollView } from 'react-native';
import { Image } from 'expo-image';
import { useTheme } from '../../../src/theme/useTheme';
import { getWcColors, getAvatarInitials, getAvatarColorByName } from './matchPickStats.helper';
import { getPickStatsMemberPicksRowStyles } from './PickStatsMemberPicksRow.styles';
import { getAvatarAsset } from '../../../src/avatars/helpers/getAvatarAsset';
import { normalizeDisplayAvatarId } from '../../../src/avatars/types/avatar.types';
import { AVATAR_PALETTE } from './MatchPickStats.constants';
import type { PickStatsMemberPick } from '../../../src/worldcup/helpers/matchPickStatsApi';

interface PickStatsMemberPicksRowProps {
  memberPicks: PickStatsMemberPick[];
  actualHomeScore: number | null;
  actualAwayScore: number | null;
  viewerUserId?: string | null;
}

function isExactHit(ph: number, pa: number, ah: number | null, aa: number | null): boolean {
  return ah !== null && aa !== null && ph === ah && pa === aa;
}

const MemberAvatar: React.FC<{
  pick: PickStatsMemberPick;
  styles: ReturnType<typeof getPickStatsMemberPicksRowStyles>;
}> = ({ pick, styles }) => {
  const avatarId = normalizeDisplayAvatarId(pick.avatarId);
  const source = getAvatarAsset(avatarId, 'idle', true);

  if (source) {
    return (
      <Image
        source={source}
        style={styles.memberPickAvatar}
        contentFit="cover"
        cachePolicy="memory-disk"
      />
    );
  }

  return (
    <View style={[styles.memberPickAvatarFallback, { backgroundColor: getAvatarColorByName(pick.displayName, AVATAR_PALETTE) }]}>
      <Text style={styles.memberPickAvatarText}>{getAvatarInitials(pick.displayName)}</Text>
    </View>
  );
};

export const PickStatsMemberPicksRow: React.FC<PickStatsMemberPicksRowProps> = ({
  memberPicks,
  actualHomeScore,
  actualAwayScore,
  viewerUserId,
}) => {
  const theme = useTheme();
  const C = useMemo(() => getWcColors(theme), [theme]);
  const styles = useMemo(() => getPickStatsMemberPicksRowStyles(C), [C]);

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      style={styles.memberPicksScroll}
      contentContainerStyle={styles.memberPicksContent}
    >
      {memberPicks.map((pick) => {
        const exact = isExactHit(
          pick.predictedHomeScore,
          pick.predictedAwayScore,
          actualHomeScore,
          actualAwayScore,
        );
        const isMe = viewerUserId != null && pick.userId === viewerUserId;

        return (
          <View
            key={pick.userId}
            style={[styles.memberPickCard, isMe && styles.memberPickCardMe]}
          >
            <MemberAvatar pick={pick} styles={styles} />
            <Text style={styles.memberPickName} numberOfLines={1}>
              {pick.displayName}
            </Text>
            <Text style={styles.memberPickScore}>
              {pick.predictedHomeScore}–{pick.predictedAwayScore}
            </Text>
            {exact ? (
              <View style={styles.memberPickExactBadge}>
                <Text style={styles.memberPickExactBadgeText}>Exact</Text>
              </View>
            ) : null}
          </View>
        );
      })}
    </ScrollView>
  );
};
